import type { ProductSearchParams } from "@/types/product.dto";
import type { NearbyStoreRequest } from "@/types/store.dto";
import type { OrderFilterParams } from "@/types/order.dto";
import type { ReferralEarningFilterParams } from "@/types/referral.dto";
import type { IncomeReportFilterParams } from "@/types/income.dto";
import type { WalletTransactionFilterParams, SettlementFilterParams } from "@/types/wallet.dto";
import type { PaymentFilterParams } from "@/types/payment.dto";
import type { ReferralType } from "@/types/enums";

export const queryKeys = {
  auth: {
    context: ["auth", "context"] as const,
  },
  products: {
    all: ["products"] as const,
    list: (params: ProductSearchParams) => ["products", "list", params] as const,
    featured: (limit: number) => ["products", "featured", limit] as const,
    detail: (id: number) => ["products", "detail", id] as const,
    byStore: (storeId: number, page: number, pageSize: number) =>
      ["products", "store", storeId, page, pageSize] as const,
    byCategory: (categoryId: number, page: number, pageSize: number) =>
      ["products", "category", categoryId, page, pageSize] as const,
  },
  categories: {
    tree: ["categories", "tree"] as const,
    root: ["categories", "root"] as const,
    detail: (id: number) => ["categories", "detail", id] as const,
    admin: (page: number, pageSize: number) => ["categories", "admin", page, pageSize] as const,
  },
  stores: {
    nearby: (request: NearbyStoreRequest) => ["stores", "nearby", request] as const,
    detail: (id: number) => ["stores", "detail", id] as const,
    all: (page: number, pageSize: number) => ["stores", "all", page, pageSize] as const,
    my: ["stores", "my"] as const,
    dashboard: (storeId: number) => ["stores", storeId, "dashboard"] as const,
    bv: (storeId: number) => ["stores", storeId, "bv"] as const,
    bvHistory: (storeId: number, page: number, pageSize: number) =>
      ["stores", storeId, "bv-history", page, pageSize] as const,
  },
  store: {
    orders: (storeId: number, filter: OrderFilterParams) => ["store", storeId, "orders", filter] as const,
    products: (storeId: number, page: number, pageSize: number) =>
      ["store", storeId, "products", page, pageSize] as const,
    lowStock: (storeId: number) => ["store", storeId, "low-stock"] as const,
    earnings: (storeId: number, page: number, pageSize: number) =>
      ["store", storeId, "earnings", page, pageSize] as const,
  },
  orders: {
    my: (filter: OrderFilterParams) => ["orders", "my", filter] as const,
    all: (filter: OrderFilterParams) => ["orders", "all", filter] as const,
    detail: (id: number) => ["orders", "detail", id] as const,
    byNumber: (orderNumber: string) => ["orders", "number", orderNumber] as const,
  },
  wallets: {
    my: ["wallets", "my"] as const,
    adminUser: (userId: string) => ["wallets", "admin-user", userId] as const,
    transactions: (walletId: number, filter: WalletTransactionFilterParams) =>
      ["wallets", walletId, "transactions", filter] as const,
    myTransactions: (filter: WalletTransactionFilterParams) =>
      ["wallets", "my-transactions", filter] as const,
    adminTransactions: (filter: WalletTransactionFilterParams) =>
      ["wallets", "admin-transactions", filter] as const,
  },
  referrals: {
    summary: ["referrals", "summary"] as const,
    link: ["referrals", "link"] as const,
    network: (maxLevel: number) => ["referrals", "network", maxLevel] as const,
    earnings: (filter: ReferralEarningFilterParams) => ["referrals", "earnings", filter] as const,
  },
  income: {
    summary: ["income", "summary"] as const,
    entries: (filter: IncomeReportFilterParams) => ["income", "entries", filter] as const,
    adminSummary: ["income", "admin-summary"] as const,
    adminEntries: (filter: IncomeReportFilterParams) =>
      ["income", "admin-entries", filter] as const,
    adminDistribution: (filter: Record<string, unknown>) =>
      ["income", "admin-distribution", filter] as const,
  },
  admin: {
    stores: (page: number, pageSize: number) => ["admin", "stores", page, pageSize] as const,
    pendingStores: ["admin", "stores", "pending"] as const,
    products: ["admin", "products"] as const,
    pendingProducts: ["admin", "products", "pending"] as const,
    deliveryPartners: (page: number, pageSize: number) => ["admin", "delivery", page, pageSize] as const,
    settlements: (filter: SettlementFilterParams) => ["admin", "settlements", filter] as const,
    referralLevels: (type: ReferralType) => ["admin", "referral-levels", type] as const,
    payments: (filter: PaymentFilterParams) => ["admin", "payments", filter] as const,
    users: (params: import("@/types/admin-user.dto").UserFilterParams) => ["admin", "users", params] as const,
  },
  delivery: {
    profile: ["delivery", "profile"] as const,
    dashboard: (partnerId: number) => ["delivery", partnerId, "dashboard"] as const,
    orders: (partnerId: number, filter: OrderFilterParams) =>
      ["delivery", partnerId, "orders", filter] as const,
    earnings: (partnerId: number, page: number, pageSize: number) =>
      ["delivery", partnerId, "earnings", page, pageSize] as const,
  },
  addresses: {
    all: ["addresses"] as const,
    detail: (id: number) => ["addresses", "detail", id] as const,
  },
  payments: {
    byOrder: (orderId: number) => ["payments", "order", orderId] as const,
  },
  banners: {
    list: (filter: import("@/types/banner.dto").BannerFilterParams) => ["banners", filter] as const,
    admin: (filter: import("@/types/banner.dto").BannerFilterParams) => ["banners", "admin", filter] as const,
  },
  coupons: {
    admin: (filter: import("@/types/coupon.dto").CouponFilterParams) => ["coupons", "admin", filter] as const,
  },
  cms: {
    page: (slug: string) => ["cms", "page", slug] as const,
    admin: (page: number, pageSize: number) => ["cms", "admin", page, pageSize] as const,
  },
  customer: {
    member: ["customer", "member"] as const,
    coupons: ["customer", "coupons"] as const,
  },
  notifications: {
    admin: (filter: import("@/types/notification.dto").NotificationFilterParams) =>
      ["notifications", "admin", filter] as const,
    me: (filter: import("@/types/notification.dto").NotificationFilterParams) =>
      ["notifications", "me", filter] as const,
    unreadCount: (scope: "admin" | "me") => ["notifications", scope, "unread-count"] as const,
  },
  settings: {
    backups: ["settings", "backups"] as const,
    platform: ["settings", "platform"] as const,
    email: ["settings", "email"] as const,
    emailStats: ["settings", "email", "stats"] as const,
    integrations: ["settings", "integrations"] as const,
  },
  rankReward: {
    all: ["rankReward", "all"] as const,
    detail: (id: number) => ["rankReward", "detail", id] as const,
    myStatus: ["rankReward", "myStatus"] as const,
    adminRequests: ["rankReward", "adminRequests"] as const,
  },
};
